import { css, cx } from '@emotion/css';
import { TaskLog } from 'services/api/log';
import { Button, Space, Typography } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import dayjs from 'lib/dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isUndefined } from 'lodash-es';
// @ts-ignore
import Mark from 'mark.js';
import { useTranslate } from '@tolgee/react';

const { Paragraph } = Typography;

export interface LogItemProps {
  data: TaskLog;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  searchWord?: string;
  round?: number;
}

export interface LogItemLayoutProps extends LogItemProps {
  title: React.ReactNode;
  before?: React.ReactNode;
  after?: React.ReactNode;
  removeStore?: string;
  addStore?: string;
}

export const EllipsisContent = (props: { text: string }) => {
  const { text } = props;
  const { t } = useTranslate();
  const [trigger, setTrigger] = useState(-1);
  const [isEllipsis, setIsEllipsis] = useState(true);

  const onEllipsis = () => {
    setTrigger(trigger + 1);
    setIsEllipsis(true);
  };

  const handleClick = () => {
    setIsEllipsis((v) => !v);
  };

  return (
    <div>
      <Paragraph className="!mb-0" ellipsis={isEllipsis ? { rows: 2, onEllipsis } : false}>
        {text}
      </Paragraph>
      {trigger >= 0 && (
        <Button className="!p-0" type="link" onClick={handleClick}>
          {isEllipsis ? (
            <span>
              {t('phrase.展开')}
              <DownOutlined className="align-baseline" />
            </span>
          ) : (
            <span>
              {t('phrase.收起')}
              <UpOutlined className="align-baseline" />
            </span>
          )}
        </Button>
      )}
    </div>
  );
};

export const LogItemLayout = (props: LogItemLayoutProps) => {
  const { data, title, before, after, removeStore, addStore, searchWord } = props;
  const { operatorId, operatorAccount, createTime } = data;
  const [hide, setHide] = useState(false);
  const { t } = useTranslate();

  const showDetail = useMemo(() => {
    return !isUndefined(before) || !isUndefined(after);
  }, [before, after]);

  const showStoreOperation = useMemo(() => {
    return !isUndefined(removeStore) || !isUndefined(addStore);
  }, [removeStore, addStore]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let marker: any;
    if (searchWord) {
      if (containerRef?.current?.innerText?.includes(searchWord)) {
        setHide(false);
        marker = new Mark(containerRef.current);
        marker.markRegExp(new RegExp(`${searchWord}`, 'gmi'));
      } else {
        setHide(true);
      }
    } else {
      setHide(false);
    }

    return () => {
      if (marker) {
        marker.unmark();
      }
      marker = null;
    };
  }, [searchWord]);

  const operator = useMemo(() => {
    if (operatorId === -1) {
      // 系统
      return t('TaskOperationLog.system');
    }
    if (operatorId === 0) {
      // 魔法门
      return t('TaskOperationLog.leyan');
    }
    if (operatorAccount === 'supervisor') {
      return t('department.超级管理员');
    }
    return operatorAccount;
  }, [operatorId, operatorAccount, t]);

  return (
    <div
      ref={containerRef}
      className={cx(container, 'relative', 'pl-5', 'pb-4', hide ? 'hidden' : '')}
      data-hidden={hide}
    >
      <div className="flex gap-2 justify-between items-start">
        <Space align="start">
          <span className="whitespace-nowrap">{operator}</span>
          <span className="pr-16">{title}</span>
        </Space>
        <div className="text-xs text-content-200 whitespace-nowrap leading-5">
          {dayjs(createTime).format('YYYY-MM-DD HH:mm:ss')}
        </div>
      </div>
      {showDetail && (
        <div className="flex flex-col p-3 mt-2 text-content-200 bg-fill-100 ">
          <Space align="start">
            <div className="whitespace-nowrap">{t('TaskOperationLog.before')}</div>
            <div>{before}</div>
          </Space>
          <Space align="start">
            <div className="whitespace-nowrap">{t('TaskOperationLog.after')}</div>
            <div>{after}</div>
          </Space>
        </div>
      )}
      {showStoreOperation && (
        <div className="flex flex-col p-3 mt-2 text-content-200 bg-fill-100">
          {addStore && (
            <div>
              <EllipsisContent text={t('TaskOperationLog.addStore', { text: addStore })} />
            </div>
          )}
          {removeStore && (
            <div>
              <EllipsisContent text={t('TaskOperationLog.removeStore', { text: removeStore })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const container = css`
  &:before {
    content: '';
    display: inline-block;
    position: absolute;
    width: 9px;
    height: 9px;
    background-color: #d3d7e0;
    border-radius: 50%;
    left: 0;
    top: 8px;
  }

  &:after {
    content: '';
    display: inline-block;
    position: absolute;
    height: 100%;
    border-left: 1px;
    border-width: 1px;
    border-style: dotted;
    border-color: var(--fett-border-strong);
    left: 4px;
    top: 0;
  }

  &:first-of-type:after {
    top: 8px;
  }

  mark {
    background-color: rgb(252, 240, 173);
  }
`;
